import { useCallback, useMemo, useState, type PropsWithChildren } from 'react';
import { AnnouncerContext, type Politeness } from '@/contexts/AnnouncerContext';

/**
 * A single live region for the whole app.
 *
 * Visual feedback (a row appearing, a total changing) is invisible to screen
 * readers, so actions that only change state — undo, redo, autosave, adding a
 * service from the palette — announce themselves here.
 */
export function AnnouncerProvider({ children }: PropsWithChildren) {
  const [polite, setPolite] = useState('');
  const [assertive, setAssertive] = useState('');

  const announce = useCallback((message: string, politeness: Politeness = 'polite') => {
    const setter = politeness === 'assertive' ? setAssertive : setPolite;
    // Clearing first guarantees repeated identical messages are re-announced.
    setter('');
    window.setTimeout(() => setter(message), 40);
  }, []);

  const value = useMemo(() => ({ announce }), [announce]);

  return (
    <AnnouncerContext.Provider value={value}>
      {children}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {polite}
      </div>
      <div aria-live="assertive" aria-atomic="true" className="sr-only">
        {assertive}
      </div>
    </AnnouncerContext.Provider>
  );
}
